import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Section, Overline, Field, CTAButton } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { catalogForLocale, validCatalog } from "@/data/catalog";
import { freeQuestionnaireForLocale, visibleQuestions } from "@/data/questionnaire";
import { triageFlags, TRIAGE_COPY } from "@/data/triage";
import { computeScore, SCORE_COPY } from "@/data/score";
import ScoreBoard from "@/components/ScoreBoard";
import { saveReading } from "@/lib/readings";
import { toast } from "sonner";

const FREE_DIAGNOSTIC_COPY = {
  fr: {
    overline: "Diagnostic capillaire offert",
    context: "Cette première lecture s'appuie uniquement sur vos réponses. Elle permet de situer vos habitudes et vos priorités avant toute mesure de la fibre.",
    resultOverline: "Votre première lecture",
    resultTitle: "Votre profil capillaire déclaratif est prêt.",
    resultBody: "Trois repères ressortent de vos réponses. Ils orientent la suite du parcours sans remplacer une analyse de la fibre.",
    exposure: "Exposition de la fibre",
    fibre: "Ressenti de la fibre",
    scalp: "Contexte du cuir chevelu",
    low: "Faible",
    moderate: "Modéré",
    marked: "Marqué",
    exposureBody: "Coloration, décoloration, lissage et chaleur déclarés.",
    fibreBody: "Casse et sécheresse ressenties au quotidien.",
    scalpBody: "Chute, inconfort ou pellicules déclarés.",
    create: "Créer mon profil et suivre mon évolution",
    createNote: "Créez votre profil gratuitement pour conserver cet indice, le retrouver à chaque nouvelle lecture et suivre votre évolution dans le temps — en attendant l'analyse approfondie de votre fibre en laboratoire, avec le kit.",
    kit: "Découvrir le parcours du kit",
    restart: "Recommencer",
    stepsLabel: "Progression",
    disclaimer: "Lecture indicative et non médicale, fondée uniquement sur vos déclarations. Aucun diagnostic clinique ni résultat de laboratoire n'est produit à cette étape.",
  },
  en: {
    overline: "Complimentary hair diagnostic",
    context: "This first reading is based solely on your answers. It helps frame your habits and priorities before any fibre measurement.",
    resultOverline: "Your first reading",
    resultTitle: "Your declarative hair profile is ready.",
    resultBody: "Three reference points stand out from your answers. They help shape the next step without replacing a fibre analysis.",
    exposure: "Fibre exposure",
    fibre: "Fibre feel",
    scalp: "Scalp context",
    low: "Low",
    moderate: "Moderate",
    marked: "Marked",
    exposureBody: "Declared colour, bleach, smoothing and heat habits.",
    fibreBody: "Breakage and dryness experienced day to day.",
    scalpBody: "Declared shedding, discomfort or flaking.",
    create: "Create my profile and track my evolution",
    createNote: "Create your profile for free to keep this index, find it again at every new reading and follow your evolution over time — while waiting for the in-depth laboratory analysis of your fibre, with the kit.",
    kit: "Discover the kit journey",
    restart: "Start again",
    stepsLabel: "Progress",
    disclaimer: "Indicative, non-medical reading based solely on your declarations. No clinical diagnosis or laboratory result is produced at this stage.",
  },
};

const groupLevel = (answers, questions, ids, copy) => {
  const values = ids.map((id) => {
    const question = questions.find((item) => item.id === id);
    const index = question?.options.indexOf(answers[id]);
    return index >= 0 && question.options.length > 1 ? index / (question.options.length - 1) : 0;
  });
  const average = values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
  if (average < 0.34) return copy.low;
  if (average < 0.67) return copy.moderate;
  return copy.marked;
};

export function FreeDiagnostic() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [complete, setComplete] = useState(false);
  const { t, locale } = useI18n();
  const copy = FREE_DIAGNOSTIC_COPY[locale] || FREE_DIAGNOSTIC_COPY.fr;

  useEffect(() => {
    setAllQuestions(freeQuestionnaireForLocale(locale));
    setStep(0);
    setAnswers({});
    setComplete(false);
    api.get("/questionnaire", { params: { locale } })
      .then(({ data }) => { if (Array.isArray(data?.questions) && data.questions.length) setAllQuestions(data.questions); })
      .catch(() => {});
  }, [locale]);

  // Le parcours s'adapte : une branche non pertinente disparaît de la liste.
  const questions = visibleQuestions(allQuestions, answers);
  const total = questions.length;

  // Changer une réponse peut retirer des questions : l'index doit rester valide.
  useEffect(() => {
    if (total && step > total - 1) setStep(total - 1);
  }, [step, total]);

  const current = questions[step];

  // Les sections servent de repères de progression, plus lisibles qu'une liste d'étapes.
  const sections = questions.reduce((acc, question, index) => {
    const last = acc[acc.length - 1];
    if (last && last.name === question.section) last.indexes.push(index);
    else acc.push({ name: question.section, indexes: [index] });
    return acc;
  }, []);

  const pick = (value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);

    // La liste est recalculée après la réponse : la question suivante peut avoir changé.
    const nextQuestions = visibleQuestions(allQuestions, next);
    const position = nextQuestions.findIndex((question) => question.id === current.id);
    if (position >= 0 && position + 1 < nextQuestions.length) return setStep(position + 1);

    try {
      window.sessionStorage.setItem("docthair_free_diagnostic", JSON.stringify({ answers: next, locale }));
    } catch {
      // The result remains available in memory when storage is unavailable.
    }
    // La lecture rejoint l'historique local : elle servira de point de départ
    // au suivi dès que l'utilisateur créera son profil.
    saveReading({ answers: next, score: computeScore(nextQuestions, next), locale, synced: false });
    setComplete(true);
    window.scrollTo(0, 0);
  };

  const restart = () => { setAnswers({}); setStep(0); setComplete(false); };

  if (complete) {
    const flags = triageFlags(questions, answers);
    const triage = TRIAGE_COPY[locale] || TRIAGE_COPY.fr;
    const score = computeScore(questions, answers);
    const scoreCopy = SCORE_COPY[locale] || SCORE_COPY.fr;

    return (
      <Section className="py-16 md:py-24" data-testid="free-diagnostic-result">
        {/* Orientation médicale : posée avant le score, pour qu'elle ne soit
            jamais lue comme une conséquence de l'indice. */}
        {flags.length > 0 && (
          <div className="mb-14 max-w-3xl border-l-2 border-black bg-[#F8F9FA] p-6 md:p-8" data-testid="free-diagnostic-triage" role="status">
            <p className="flex items-start gap-3 font-display text-xl font-light leading-snug">
              <AlertCircle className="mt-1 h-5 w-5 shrink-0" aria-hidden="true" />
              {triage.title}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-zinc-600">{triage.body}</p>
          </div>
        )}

        <ScoreBoard score={score} copy={scoreCopy} />

        <p className="mt-12 max-w-2xl text-sm leading-relaxed text-zinc-600">{copy.createNote}</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <CTAButton to="/register" testId="free-diagnostic-register-btn">{copy.create}</CTAButton>
          <CTAButton to="/kit" testId="free-diagnostic-kit-btn" variant="ghost">{copy.kit}</CTAButton>
        </div>
        <button type="button" onClick={restart} className="mt-7 text-sm font-semibold text-zinc-500 hover:text-black" data-testid="free-diagnostic-restart-btn">
          {copy.restart}
        </button>
        <p className="mt-10 max-w-3xl border-t border-black/10 pt-5 text-[11px] leading-relaxed text-zinc-400">{scoreCopy.disclaimer}</p>
      </Section>
    );
  }

  if (!current) {
    return <Section className="py-32"><p className="flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</p></Section>;
  }

  return (
    <div className="relative min-h-[calc(100vh-72px)] dt-noise" data-testid="free-diagnostic-questionnaire">
      <div className="absolute inset-0 dt-grid-lines opacity-50" aria-hidden="true" />

      <div className="relative h-[2px] w-full bg-zinc-100">
        <div className="h-full bg-black transition-all duration-700 ease-out" style={{ width: `${((step + 1) / total) * 100}%` }} data-testid="free-diagnostic-progress" />
      </div>

      {/* Repère compact réservé au mobile, où le rail latéral est masqué. */}
      <div className="relative flex items-center justify-between border-b border-black/10 px-6 py-4 md:hidden">
        <span className="font-mono-dt text-[10px] uppercase tracking-wider text-zinc-400">{current.section}</span>
        <span className="font-mono-dt text-[10px] text-zinc-400">
          {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>

      <Section className="relative grid gap-12 py-10 md:grid-cols-12 md:py-24">
        <div className="md:col-span-4">
          <Overline>{copy.overline}</Overline>
          <p className="mt-4 hidden font-mono-dt text-xs text-zinc-400 md:block">
            {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
          <p className="mt-8 hidden max-w-xs text-sm leading-relaxed text-zinc-500 md:block">{copy.context}</p>

          {/* Jalons par section : les étapes déjà atteintes restent accessibles d'un clic. */}
          <nav aria-label={copy.stepsLabel} className="mt-10 hidden md:block" data-testid="free-diagnostic-steps">
            <ol className="space-y-3 border-l border-black/10 pl-5">
              {sections.map((section) => {
                const isCurrent = section.indexes.includes(step);
                const isDone = section.indexes.every((index) => index < step);
                const target = section.indexes.find((index) => index <= step);
                const isReachable = target !== undefined;
                const answered = section.indexes.filter((index) => answers[questions[index].id]).length;

                return (
                  <li key={section.name} className="relative">
                    <span
                      className={`absolute -left-[23px] top-[7px] h-1.5 w-1.5 transition-colors ${
                        isCurrent ? "bg-black" : isDone ? "bg-black/40" : "bg-black/10"
                      }`}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      disabled={!isReachable}
                      aria-current={isCurrent ? "step" : undefined}
                      onClick={() => isReachable && setStep(target)}
                      data-testid={`free-diagnostic-section-${section.name}`}
                      className={`flex w-full items-baseline justify-between gap-3 text-left font-mono-dt text-[10px] uppercase tracking-wider transition-colors ${
                        isCurrent ? "text-black" : isReachable ? "text-zinc-400 hover:text-black" : "cursor-default text-zinc-300"
                      }`}
                    >
                      <span>{section.name}</span>
                      <span className="text-[9px] tabular-nums">{answered}/{section.indexes.length}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          {step > 0 && (
            <button data-testid="free-diagnostic-back-btn" onClick={() => setStep(step - 1)} className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 transition-colors hover:text-black">
              <ArrowLeft className="h-4 w-4" /> {t("common.previous")}
            </button>
          )}
        </div>

        <div className="md:col-span-7 md:col-start-6">
          {/* La clé relance l'animation d'apparition à chaque question. */}
          <div key={current.id} className="dt-reveal">
            <h1 className="max-w-2xl font-display text-3xl font-light leading-[1.05] tracking-tight sm:text-4xl md:text-5xl">{current.question}</h1>

            {/* Pourquoi la question compte : l'utilisateur comprend ce qu'il donne. */}
            {current.why && (
              <p className="mt-5 max-w-xl border-l border-black/15 pl-4 text-sm leading-relaxed text-zinc-500">{current.why}</p>
            )}

            <div role="radiogroup" aria-label={current.question} className="mt-9 grid gap-3">
              {current.options.map((option, index) => {
                const isSelected = answers[current.id] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    data-testid={`free-diagnostic-option-${current.id}-${index}`}
                    onClick={() => pick(option)}
                    className={`group flex min-h-[64px] items-center justify-between gap-6 border px-6 py-5 text-left transition-all duration-200 ${
                      isSelected
                        ? "border-black bg-black text-white"
                        : "border-black/15 bg-white hover:border-black hover:bg-[#F8F9FA] active:bg-[#F1F3F5]"
                    }`}
                  >
                    <span className="flex items-center gap-5">
                      <span className={`font-mono-dt text-[10px] transition-colors ${isSelected ? "text-white/60" : "text-zinc-400"}`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base sm:text-lg">{option}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export function Questionnaire() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [saved, setSaved] = useState(false);
  const { t, locale } = useI18n();

  useEffect(() => {
    // Les questions locales servent de base : le parcours reste utilisable même
    // lorsque l'API ne répond pas. Sans cela, une réponse inattendue laissait
    // `questions` indéfini et la page se vidait entièrement.
    setAllQuestions(freeQuestionnaireForLocale(locale));
    api.get("/questionnaire", { params: { locale } })
      .then(({ data }) => { if (Array.isArray(data?.questions) && data.questions.length) setAllQuestions(data.questions); })
      .catch(() => {});
  }, [locale]);

  // Même parcours adaptatif que le pré-diagnostic public.
  const questions = visibleQuestions(allQuestions, answers);
  const total = questions.length;

  useEffect(() => {
    if (total && step > total - 1) setStep(total - 1);
  }, [step, total]);

  const current = questions[step];

  const pick = async (value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);

    const nextQuestions = visibleQuestions(allQuestions, next);
    const position = nextQuestions.findIndex((question) => question.id === current.id);
    if (position >= 0 && position + 1 < nextQuestions.length) return setStep(position + 1);

    // Fin du parcours. L'indice appartient à l'utilisateur : il s'affiche
    // toujours, y compris si l'enregistrement serveur échoue. Auparavant une
    // erreur d'API laissait la personne sans résultat après 19 questions.
    setBusy(true);
    const reading = computeScore(nextQuestions, next);
    let synced = false;
    try {
      // L'indice est envoyé avec les réponses : le suivi dans le temps
      // compare des lectures complètes, pas seulement des réponses brutes.
      await api.post("/me/questionnaire", { answers: next, score: reading, locale });
      synced = true;
    } catch {
      // L'enregistrement distant a échoué : on conserve la lecture
      // localement plutôt que d'interrompre le parcours.
    }
    saveReading({ answers: next, score: reading, locale, synced });
    setSaved(synced);
    setBusy(false);
    setComplete(true);
    window.scrollTo(0, 0);
  };

  if (complete) {
    const score = computeScore(questions, answers);
    const scoreCopy = SCORE_COPY[locale] || SCORE_COPY.fr;
    const flags = triageFlags(questions, answers);
    const triage = TRIAGE_COPY[locale] || TRIAGE_COPY.fr;

    return (
      <Section className="py-16 md:py-24" data-testid="quiz-result">
        {flags.length > 0 && (
          <div className="mb-14 max-w-3xl border-l-2 border-black bg-[#F8F9FA] p-6 md:p-8" role="status">
            <p className="flex items-start gap-3 font-display text-xl font-light leading-snug">
              <AlertCircle className="mt-1 h-5 w-5 shrink-0" aria-hidden="true" />
              {triage.title}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-zinc-600">{triage.body}</p>
          </div>
        )}

        <ScoreBoard score={score} copy={scoreCopy} />

        <p className="mt-12 max-w-2xl text-sm leading-relaxed text-zinc-600" data-testid="quiz-storage-note">
          {saved ? scoreCopy.savedRemote : scoreCopy.savedLocal}
        </p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <CTAButton to="/kit" testId="quiz-kit-btn">{scoreCopy.kitCta}</CTAButton>
          <CTAButton to="/account" testId="quiz-account-btn" variant="ghost">{scoreCopy.accountCta}</CTAButton>
        </div>
        <p className="mt-10 max-w-3xl border-t border-black/10 pt-5 text-[11px] leading-relaxed text-zinc-400">{scoreCopy.disclaimer}</p>
      </Section>
    );
  }

  if (!current)
    return <Section className="py-32"><p className="flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</p></Section>;

  return (
    <div className="relative min-h-[calc(100vh-72px)] dt-noise">
      <div className="absolute inset-0 dt-grid-lines opacity-50" aria-hidden="true" />
      <div className="relative h-[2px] w-full bg-zinc-100">
        <div className="h-full bg-black transition-all duration-500" style={{ width: `${(step / total) * 100}%` }} data-testid="quiz-progress" />
      </div>
      <Section className="relative grid gap-12 py-14 md:grid-cols-12 md:py-24">
        <div className="md:col-span-4">
          <Overline>{current.section}</Overline>
          <p className="mt-4 font-mono-dt text-xs text-zinc-400">
            {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
          <p className="mt-8 max-w-xs text-sm leading-relaxed text-zinc-500">
            {t("quiz.context")}
          </p>
          {step > 0 && (
            <button data-testid="quiz-back-btn" onClick={() => setStep(step - 1)} className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-black">
              <ArrowLeft className="h-4 w-4" /> {t("common.previous")}
            </button>
          )}
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <div key={current.id} className="dt-reveal">
            <h1 className="max-w-2xl font-display text-4xl font-light leading-[1] tracking-tight sm:text-5xl">{current.question}</h1>
            {current.why && (
              <p className="mt-5 max-w-xl border-l border-black/15 pl-4 text-sm leading-relaxed text-zinc-500">{current.why}</p>
            )}
            <div className="mt-9 grid gap-3">
              {current.options.map((o, i) => (
                <button key={o} data-testid={`quiz-option-${current.id}-${i}`} onClick={() => pick(o)} disabled={busy}
                  className={`group flex items-center justify-between gap-6 border px-6 py-5 text-left transition-colors ${answers[current.id] === o ? "border-black bg-black text-white" : "border-black/15 bg-white hover:border-black hover:bg-[#F8F9FA]"}`}>
                  <span className="flex items-center gap-5">
                    <span className="font-mono-dt text-[10px] text-zinc-400">{String.fromCharCode(65 + i)}</span>
                    <span className="text-base">{o}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))}
            </div>
            {busy && <p className="mt-8 flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</p>}
          </div>
        </div>
      </Section>
    </div>
  );
}

export function Order() {
  const [types, setTypes] = useState(() => catalogForLocale("fr"));
  const [selected, setSelected] = useState("advanced");
  const [form, setForm] = useState({ full_name: "", address: "", city: "", postal_code: "", country: "FR" });
  const [busy, setBusy] = useState(false);
  const { t, locale } = useI18n();

  useEffect(() => {
    const fallback = catalogForLocale(locale);
    setTypes(fallback);
    api.get("/catalog", { params: { locale } })
      .then(({ data }) => { if (validCatalog(data)) setTypes(data.analysis_types); })
      .catch(() => {});
  }, [locale]);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        analysis_code: selected, origin_url: window.location.origin, shipping: form,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = apiError(err.response?.data?.detail, err.message);
      if (err.response?.status === 401) {
        toast.error(t("order.signin"));
        window.location.href = "/login";
        return;
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const active = types.find((a) => a.code === selected) || types[0];

  return (
    <Section className="py-14 md:py-20">
      <Overline>{t("cta.order")}</Overline>
      <h1 className="mt-6 font-display text-4xl font-light tracking-tighter sm:text-5xl">{t("analysis.title")}</h1>
      <div className="mt-12 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-7">
          <div className="grid gap-4">
            {types.map((a) => {
              const on = selected === a.code;
              return (
                <button key={a.code} type="button" data-testid={`order-plan-${a.code}`} onClick={() => setSelected(a.code)}
                  className={`border p-7 text-left transition-colors ${on ? "border-black bg-[#0A0A0A] text-white" : "border-black/15 hover:border-black"}`}>
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <p className={`overline text-[9px] ${on ? "!text-zinc-500" : ""}`}>{a.metrics.length} {t("analysis.params")} · {a.turnaround_days} {t("analysis.days")}</p>
                      <h2 className="mt-3 font-display text-2xl font-light">{a.name}</h2>
                      <p className={`mt-2 text-sm ${on ? "text-zinc-400" : "text-zinc-600"}`}>{a.tagline}</p>
                    </div>
                    <p className="font-mono-dt text-lg">€{a.price}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-5">
          <form onSubmit={pay} data-testid="order-form" className="sticky top-24 border border-black/10 p-8">
            <Overline>{t("order.delivery")}</Overline>
            <div className="mt-7 grid gap-6">
              <Field id="o-name" label={t("form.name")} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="order-full_name" />
              <Field id="o-addr" label={t("form.address")} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="order-address" />
              <div className="grid grid-cols-2 gap-4">
                <Field id="o-city" label={t("form.city")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="order-city" />
                <Field id="o-zip" label={t("form.postal")} value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} testId="order-postal_code" />
              </div>
              <Field id="o-country" label={t("form.country")} value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="order-country" />
            </div>
            <div className="mt-7 flex items-baseline justify-between border-t border-black/10 pt-5">
              <span className="text-sm text-zinc-500">{active?.name}</span>
              <span className="font-mono-dt text-lg">€{active?.price ?? "—"}</span>
            </div>
            <button type="submit" data-testid="order-pay-btn" disabled={busy}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-black px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("order.pay")}
            </button>
            <p className="mt-4 text-[11px] leading-relaxed text-zinc-400">
              {t("order.payNote")}
            </p>
          </form>
        </div>
      </div>
    </Section>
  );
}

export function PaymentResult({ ok }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [state, setState] = useState(ok ? "checking" : "cancelled");
  const [sampleId, setSampleId] = useState(null);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!ok || !sessionId) return undefined;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setSampleId(data.sample_id);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch {
        /* retry */
      }
      if (tries < 12) setTimeout(tick, 2000);
      else setState("timeout");
    };
    tick();
    return undefined;
  }, [ok, sessionId]);

  return (
    <Section className="py-24 md:py-32" data-testid="payment-result">
      <div className="max-w-xl">
        {state === "checking" && (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            <h1 className="mt-8 font-display text-4xl font-light tracking-tighter">{t("pay.checking")}</h1>
            <p className="mt-4 text-sm text-zinc-600">{t("pay.checkingBody")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-7 w-7" />
            <h1 className="mt-8 font-display text-4xl font-light tracking-tighter">{t("pay.confirmed")}</h1>
            <p className="mt-4 text-sm text-zinc-600">
              {t("pay.confirmedBody")}{sampleId ? <> — <span className="font-mono-dt">{sampleId}</span></> : null}
            </p>
            <CTAButton to="/account" testId="payment-portal-btn" className="mt-10">{t("pay.openPortal")}</CTAButton>
          </>
        )}
        {["cancelled", "failed", "timeout"].includes(state) && (
          <>
            <XCircle className="h-7 w-7 text-zinc-500" />
            <h1 className="mt-8 font-display text-4xl font-light tracking-tighter">
              {state === "cancelled" ? t("pay.cancelled") : t("pay.failed")}
            </h1>
            <p className="mt-4 text-sm text-zinc-600">{t("pay.nothing")}</p>
            <CTAButton to="/order" testId="payment-retry-btn" className="mt-10">{t("pay.back")}</CTAButton>
          </>
        )}
      </div>
    </Section>
  );
}
